import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { SafeAny } from '../../../types/safe-any';

export interface EditItemProcessDialogData {
  title: string;
  item: {
    itemType: string;
    description: string;
    lbPer1000ft?: number;
    basePrice?: number;
    pricePerLb?: number;
    pricePerUnit?: number;
    specificGravity?: number;
    constant1?: number;
    constant2?: number;
    [key: string]: SafeAny;
  };
}

@Component({
  selector: 'ciwc-edit-item-process-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
  ],
  templateUrl: './edit-item-process-dialog.component.html',
  styleUrls: ['./edit-item-process-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditItemProcessDialogComponent {
  form: FormGroup;

  constructor(
    private dialogRef: MatDialogRef<EditItemProcessDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EditItemProcessDialogData,
    private fb: FormBuilder,
  ) {
    this.form = this.fb.group({
      description: [data.item.description, Validators.required],
    });

    this.updateFormControls(data.item.itemType);
    this.populateFormWithItem(data.item);
  }

  private updateFormControls(itemType: string): void {
    switch (itemType) {
      case 'OTHER':
        this.form.addControl('pricePerUnit', this.fb.control('', Validators.required));
        break;
      case 'COPPER':
        this.form.addControl('lbPer1000ft', this.fb.control('', Validators.required));
        this.form.addControl('pricePerLb', this.fb.control('', Validators.required));
        this.form.addControl('basePrice', this.fb.control({ value: '', disabled: true }));
        break;
      case 'SINGLE_END':
        this.form.addControl('specificGravity', this.fb.control('', Validators.required));
        this.form.addControl('constant1', this.fb.control('', Validators.required));
        this.form.addControl('pricePerLb', this.fb.control('', Validators.required));
        this.form.addControl('basePrice', this.fb.control({ value: '', disabled: true }));
        break;
      case 'COMPOUNDS':
      case 'TEFLON_TAPES':
        this.form.addControl('specificGravity', this.fb.control('', Validators.required));
        this.form.addControl('pricePerLb', this.fb.control('', Validators.required));
        break;
      case 'ALL_TAPES_AND_FILLERS':
        this.form.addControl('specificGravity', this.fb.control('', Validators.required));
        this.form.addControl('constant1', this.fb.control('', Validators.required));
        this.form.addControl('constant2', this.fb.control('', Validators.required));
        this.form.addControl('pricePerLb', this.fb.control('', Validators.required));
        break;
    }
  }

  private populateFormWithItem(item: EditItemProcessDialogData['item']): void {
    switch (item.itemType) {
      case 'OTHER':
        this.form.patchValue({
          pricePerUnit: item.pricePerUnit,
        });
        break;
      case 'COPPER':
        this.form.patchValue({
          lbPer1000ft: item.lbPer1000ft,
          pricePerLb: item.pricePerLb,
          basePrice: item.basePrice,
        });
        break;
      case 'SINGLE_END':
        this.form.patchValue({
          specificGravity: item.specificGravity,
          constant1: item.constant1,
          pricePerLb: item.pricePerLb,
          basePrice: item.basePrice,
        });
        break;
      case 'COMPOUNDS':
      case 'TEFLON_TAPES':
        this.form.patchValue({
          specificGravity: item.specificGravity,
          pricePerLb: item.pricePerLb,
        });
        break;
      case 'ALL_TAPES_AND_FILLERS':
        this.form.patchValue({
          specificGravity: item.specificGravity,
          constant1: item.constant1,
          constant2: item.constant2,
          pricePerLb: item.pricePerLb,
        });
        break;
    }
  }

  onSubmit(): void {
    if (this.form.valid) {
      const formValue = this.form.getRawValue();
      this.dialogRef.close({
        ...formValue,
        itemType: this.data.item.itemType,
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
